'use client';

import { useRef } from 'react';
import Button from '@/components/sections/kit/Button';
import { REGISTER_URL } from '@/components/pricing/data';
import { routePath } from '@/lib/routes';
import { gsap, EASE_OUT } from '@/components/sections/kit/motion';
import type { CompareDossierData } from './types';
import { CONTAINER } from './bits';
import { useChoreo } from './motion';

/**
 * Closing CTA — brand-drenched panel (site pattern, per QrClosingCta).
 * Eyebrow/title/body/link are competitor-specific and come from the data
 * const (e.g. TinyURL's story is "mint new links", not "import the CSV").
 */
export default function CompareClosingCta({ data }: { data: CompareDossierData }) {
  const rootRef = useRef<HTMLElement>(null);

  useChoreo(rootRef, (root) => {
    const panel = root.querySelector('[data-cta-panel]');
    if (!panel) return;
    const tl = gsap.timeline({ paused: true, defaults: { ease: EASE_OUT } });
    tl.from(panel, { y: 30, opacity: 0, scale: 0.975, duration: 0.8 });
    tl.from(
      panel.querySelectorAll('[data-cta-item]'),
      { y: 14, opacity: 0, duration: 0.55, stagger: 0.08 },
      '-=0.4',
    );
    return tl;
  });

  return (
    <section ref={rootRef} className="pt-20 md:pt-28">
      <div className={CONTAINER}>
        <div
          data-cta-panel
          className="flex flex-col items-center gap-[18px] rounded-ref-lg bg-brand-600 px-6 py-12 text-center md:px-12 md:py-16"
        >
          <span
            data-cta-item
            className="font-jetbrains text-[11px] font-semibold tracking-[0.16em] text-white/75"
          >
            {data.closing.eyebrow}
          </span>
          <h2
            data-cta-item
            className="max-w-[680px] font-onest text-[clamp(28px,3.4vw,38px)] font-extrabold leading-[1.1] tracking-[-0.02em] text-pretty text-white"
          >
            {data.closing.title}
          </h2>
          <p
            data-cta-item
            className="max-w-[560px] text-[15.5px] leading-[1.6] text-pretty text-white/85"
          >
            {data.closing.body}
          </p>
          <div data-cta-item className="mt-2 flex flex-wrap justify-center gap-3">
            <Button variant="inverse" href={REGISTER_URL}>
              Start free &mdash; no card
            </Button>
            <Button variant="ghost-light" href={routePath('contact')}>
              Talk to sales
            </Button>
          </div>
          <a
            data-cta-item
            href={data.closing.linkHref}
            className="text-[13.5px] text-white/85 underline underline-offset-[3px] hover:text-white"
          >
            {data.closing.linkLabel}
          </a>
        </div>
      </div>
    </section>
  );
}
